// Generalised from form-builder/FormsSidebar: collapsible left nav driven by a
// `sections` prop so it can be reused by any console-style area.
import React, { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { alpha, Box, Divider, IconButton, List, ListItemButton, ListItemIcon, ListItemText, Tooltip, Typography } from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { useSnackbar } from "notistack";
import { NavItem, NavSection } from "./types";

const EXPANDED_WIDTH = 248;
const COLLAPSED_WIDTH = 72;
const STORAGE_KEY = "cm-console-sidebar-collapsed";

interface ConsoleSidebarProps {
  title?: string;
  sections: NavSection[];
}

const ConsoleSidebar: React.FC<ConsoleSidebarProps> = ({ title = "Menu", sections }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { enqueueSnackbar } = useSnackbar();

  const [collapsed, setCollapsed] = useState<boolean>(() => {
    try { return localStorage.getItem(STORAGE_KEY) === "1"; } catch { return false; }
  });

  const toggle = () =>
    setCollapsed((c) => {
      const next = !c;
      try { localStorage.setItem(STORAGE_KEY, next ? "1" : "0"); } catch { /* ignore */ }
      return next;
    });

  const isActive = (item: NavItem) => {
    if (!item.path) return false;
    return item.match ? item.match(location.pathname) : location.pathname === item.path;
  };

  const handleClick = (item: NavItem) => {
    if (item.path) navigate(item.path);
    else if (item.comingSoon) enqueueSnackbar(`${item.label} — coming soon`, { variant: "info" });
  };

  const renderItem = (item: NavItem) => {
    if (item.hidden) return null;
    const active = isActive(item);
    const button = (
      <ListItemButton
        onClick={() => handleClick(item)}
        selected={active}
        sx={{
          borderRadius: 2, mx: collapsed ? "10px" : "12px", my: "2px", minHeight: 44,
          justifyContent: collapsed ? "center" : "flex-start", px: collapsed ? 0 : "12px", color: "text.primary",
          opacity: item.comingSoon ? 0.6 : 1,
          transition: "background-color .15s ease, color .15s ease",
          "&.Mui-selected": {
            bgcolor: (t) => alpha(t.palette.primary.main, 0.12), color: "primary.main",
            "&:hover": { bgcolor: (t) => alpha(t.palette.primary.main, 0.18) }
          }
        }}
      >
        <ListItemIcon sx={{ minWidth: 0, mr: collapsed ? 0 : "12px", justifyContent: "center", color: active ? "primary.main" : "text.secondary" }}>
          {item.icon}
        </ListItemIcon>
        {!collapsed && (
          <ListItemText primary={item.label} primaryTypographyProps={{ fontSize: 14, fontWeight: active ? 700 : 500, noWrap: true }} />
        )}
      </ListItemButton>
    );
    return collapsed ? (
      <Tooltip key={item.label} title={item.label} placement="right" arrow><Box>{button}</Box></Tooltip>
    ) : (
      <Box key={item.label}>{button}</Box>
    );
  };

  return (
    <Box
      component="nav"
      sx={{
        width: collapsed ? COLLAPSED_WIDTH : EXPANDED_WIDTH, flexShrink: 0, height: "100%",
        borderRight: "1px solid", borderColor: "divider", bgcolor: "background.paper",
        display: "flex", flexDirection: "column", overflow: "hidden",
        transition: (t) => t.transitions.create("width", { duration: t.transitions.duration.shorter })
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: collapsed ? "center" : "space-between", px: collapsed ? 0 : "16px", height: 56, flexShrink: 0 }}>
        {!collapsed && <Typography variant="overline" sx={{ fontWeight: 800, color: "text.secondary", letterSpacing: 0.6 }}>{title}</Typography>}
        <Tooltip title={collapsed ? "Expand" : "Collapse"} placement="right" arrow>
          <IconButton size="small" onClick={toggle} aria-label={collapsed ? "Expand" : "Collapse"}>
            {collapsed ? <ChevronRightIcon /> : <ChevronLeftIcon />}
          </IconButton>
        </Tooltip>
      </Box>
      <Divider />
      {sections.map((section, i) => (
        <Box key={section.header ?? `section-${i}`}>
          {section.header && (
            collapsed
              ? <Divider sx={{ mx: "10px", my: "8px" }} />
              : <Typography variant="caption" sx={{ px: "20px", pt: "14px", pb: "4px", display: "block", color: "text.secondary", fontWeight: 700, letterSpacing: 0.5 }}>{section.header}</Typography>
          )}
          <List sx={{ py: "4px" }} disablePadding>{section.items.map(renderItem)}</List>
        </Box>
      ))}
    </Box>
  );
};

export default ConsoleSidebar;
